// FT3D Reach approach geometry — the pure, testable half of "move the minimum distance that first
// puts the defender within Reach, without ever entering the defender".
//
// Two server contracts meet here and they are NOT the same shape:
//   • Range   — ft_combat_center_distance_ft(): centre distance minus size credits, compared `<= reach`
//               (demos/xz3d/ft3d-combat-distance-core.js is the browser twin).
//   • Ground  — ft_ground_occupancy_shape(): every actor is a CIRCLE of size-tier base/2, and
//               ft_resolve_free_position() excludes NOTHING for the intended defender. Two circles
//               overlap when d² < (rA+rB)² − 1e-6 (demos/xz3d/ft3d-move-core.js circleCircleOverlap).
// The legal landing set is therefore the annulus  solid ≤ d ≤ boundary  around the defender, where
//   solid    = (moverBase + targetBase) / 2
//   boundary = sqrt(centreLimit² − dz²),  centreLimit = reach + credit(mover) + credit(target)
// For plain 5 ft Reach that annulus has ZERO width (boundary == solid): the mover must land in exact
// contact. Positions persist as decimal(10,3), so a candidate is only real once it survives 0.001 ft
// rounding under both server checks — evaluated here exactly as PHP would evaluate them.
//
// Nothing in this file touches the DOM, three.js, or the network. tools/test/ft3d-reach-approach-core-smoke.mjs
// pins the behaviour.

import { combatCenterDistanceFt, combatVerticalSeparationFt } from './ft3d-combat-distance-core.js';

export const FT3D_REACH_APPROACH_CONTRACT = 'ft3d-reach-approach-v3';
/** encounter_creatures.x_pos / y_pos / z_pos are decimal(10,3). */
export const FT3D_PERSISTED_POSITION_PRECISION_FT = 0.001;
/** ft3d-move-core.js circleCircleOverlap(): overlap iff d² < gap² − OVERLAP_EPS. */
const OVERLAP_EPS = 1e-6;

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Round to the persisted decimal(10,3) grid the way MySQL stores it (ties away from zero — JS
 *  Math.round ties toward +Infinity, which differs for negative half-grid values) and return the SAME
 *  double PHP will read back from the decimal string, so every later comparison here is bit-identical. */
export function persistedPositionFt(value, precisionFt = FT3D_PERSISTED_POSITION_PRECISION_FT) {
  const step = Math.max(1e-9, num(precisionFt, FT3D_PERSISTED_POSITION_PRECISION_FT));
  const decimals = Math.max(0, Math.min(9, Math.round(-Math.log10(step))));
  const raw = num(value);
  const magnitude = Math.round(Math.abs(raw) / step) * step;
  return Number((Math.sign(raw) * magnitude).toFixed(decimals)) || 0;
}

/** Server ground rule: circle of base/2 each, nothing excluded → minimum legal centre separation. */
export function reachApproachSolidSeparationFt(moverSizeFt, targetSizeFt) {
  return Math.max(0, num(moverSizeFt, 5)) / 2 + Math.max(0, num(targetSizeFt, 5)) / 2;
}

/** Outer legal radius on the floor plane. v3's target rim is added AFTER projecting
 *  the caster's range through the vertical span gap; it cannot buy extra altitude. */
export function reachApproachBoundaryRadiusFt(centerLimitFt, verticalSeparationFt = 0, targetRadiusFt = 0) {
  const radius = Math.max(0, num(targetRadiusFt));
  const limit = Math.max(0, num(centerLimitFt) - radius);
  const dz = Math.abs(num(verticalSeparationFt));
  return dz > limit ? 0 : radius + Math.sqrt(Math.max(0, limit * limit - dz * dz));
}

/** Server-exact overlap test on persisted coordinates. */
export function reachApproachOverlapsTarget(destinationFt, targetFt, solidSeparationFt) {
  const dx = num(destinationFt?.x) - num(targetFt?.x);
  const dy = num(destinationFt?.y) - num(targetFt?.y);
  const gap = Math.max(0, num(solidSeparationFt));
  return dx * dx + dy * dy < gap * gap - OVERLAP_EPS;
}

/** Server-exact range test on persisted coordinates through the shared distance twin (v3: target rim,
 *  vertical term, size credits, strict `<=` — no epsilon, or a landing that reads legal here fails
 *  Janus by a rounding hair). `verticalSeparationFt` is the RAW centre dz. */
export function reachApproachWithinReach(destinationFt, targetFt, {
  verticalSeparationFt = 0,
  sourceSizeFeet = 5,
  targetSizeFeet = 5,
  reachFt,
} = {}) {
  const distance = combatCenterDistanceFt(
    { x: num(destinationFt?.x), y: num(destinationFt?.y), z: 0 },
    { x: num(targetFt?.x), y: num(targetFt?.y), z: num(verticalSeparationFt) },
    { sourceSizeFeet, targetSizeFeet, depthAuthored: true },
  );
  return distance <= num(reachFt);
}

export function segmentDistanceToPointFt(a, b, point) {
  const ax = num(a?.[0] ?? a?.x);
  const ay = num(a?.[1] ?? a?.y);
  const bx = num(b?.[0] ?? b?.x);
  const by = num(b?.[1] ?? b?.y);
  const px = num(point?.x);
  const py = num(point?.y);
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared <= 1e-12
    ? 0
    : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

/** Smallest distance from any route segment to the target centre (Infinity for a degenerate route). */
export function routeClearanceFromTargetFt(routeFt, targetFt) {
  if (!Array.isArray(routeFt) || routeFt.length < 2) return Number.POSITIVE_INFINITY;
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < routeFt.length; index += 1) {
    minimum = Math.min(minimum, segmentDistanceToPointFt(routeFt[index - 1], routeFt[index], targetFt));
  }
  return minimum;
}

/**
 * The invariant every accepted approach must satisfy: the landing footprint AND every route segment
 * keep the mover's centre at least `solidSeparationFt` from the defender's centre. Contact at the
 * boundary is legal (the server's own overlap test says so); entering is not.
 */
export function reachApproachClearance({ destinationFt, routeFt, targetFt, solidSeparationFt }) {
  const solid = Math.max(0, num(solidSeparationFt));
  const destinationDistanceFt = Math.hypot(num(destinationFt?.x) - num(targetFt?.x), num(destinationFt?.y) - num(targetFt?.y));
  const routeMinDistanceFt = routeClearanceFromTargetFt(routeFt, targetFt);
  const destinationClears = !reachApproachOverlapsTarget(destinationFt, targetFt, solid);
  // A route sample may graze the boundary by float noise; anything deeper than the server's overlap
  // tolerance is an entry.
  const routeClears = routeMinDistanceFt * routeMinDistanceFt >= solid * solid - OVERLAP_EPS;
  return {
    destinationDistanceFt,
    destinationClears,
    routeMinDistanceFt,
    routeClears,
    clears: destinationClears && routeClears,
  };
}

/**
 * Persisted-grid points at EXACT centre distance `radiusFt` from the target. Needed when the legal
 * annulus is degenerate (plain 5 ft Reach): only lattice points with k² + m² = N survive rounding.
 * Brute force over one octant of the 0.001 grid (≤ 5 000 steps for a 5 ft radius).
 */
export function reachApproachExactLatticeOffsets(radiusFt, precisionFt = FT3D_PERSISTED_POSITION_PRECISION_FT) {
  const step = Math.max(1e-9, num(precisionFt, FT3D_PERSISTED_POSITION_PRECISION_FT));
  const scale = 1 / step;
  const n = Math.round(num(radiusFt) * scale);
  if (n <= 0) return [];
  const target = n * n;
  const offsets = [];
  const limit = Math.floor(n / Math.SQRT2) + 1;
  for (let k = 0; k <= limit && k <= n; k += 1) {
    const rest = target - k * k;
    if (rest < 0) break;
    const m = Math.round(Math.sqrt(rest));
    if (m * m !== rest || m < k) continue;
    const a = k / scale;
    const b = m / scale;
    const unique = new Set();
    for (const [x, y] of [[a, b], [b, a], [-a, b], [-b, a], [a, -b], [b, -a], [-a, -b], [-b, -a]]) {
      const key = `${x.toFixed(3)}:${y.toFixed(3)}`;
      if (unique.has(key)) continue;
      unique.add(key);
      offsets.push({ x, y });
    }
  }
  return offsets;
}

/**
 * Ordered landing candidates. Rings: the outer boundary first (minimum movement), then inset rings
 * only while they stay outside the solid separation, and finally the contact ring itself when the
 * boundary sits above it. NOTHING below `solidSeparationFt` is ever generated.
 *
 * Every candidate is snapped to the persisted grid and re-validated under the server's own two
 * checks; a candidate that would be nudged or fall out of reach after storage is discarded here,
 * not discovered by the player after the move.
 */
export function reachApproachCandidates({
  originFt,
  targetFt,
  boundaryRadiusFt,
  solidSeparationFt,
  verticalSeparationFt = 0,
  sourceSizeFeet = 5,
  targetSizeFeet = 5,
  reachFt,
  angleCount = 32,
  insetsFt = [0.5, 1],
  precisionFt = FT3D_PERSISTED_POSITION_PRECISION_FT,
}) {
  const ox = num(originFt?.x);
  const oy = num(originFt?.y);
  const tx = num(targetFt?.x);
  const ty = num(targetFt?.y);
  const boundary = Math.max(0, num(boundaryRadiusFt));
  const solid = Math.max(0, num(solidSeparationFt));
  const empty = reason => ({ candidates: [], rings: [], reason });
  if (Math.hypot(tx - ox, ty - oy) <= 1e-6) return empty('coincident');
  if (boundary + 1e-9 < solid) return empty('no-legal-boundary');

  const rings = [{ radiusFt: boundary, kind: 'boundary' }];
  for (const inset of insetsFt) {
    const radius = boundary - Math.max(0, num(inset));
    if (radius >= solid - 1e-9 && radius < boundary - 1e-9) rings.push({ radiusFt: radius, kind: 'inset' });
  }
  if (boundary - solid > 1e-9 && !rings.some(ring => Math.abs(ring.radiusFt - solid) <= 1e-9)) {
    rings.push({ radiusFt: solid, kind: 'contact' });
  }

  const nearestAngle = Math.atan2(oy - ty, ox - tx);
  const count = Math.max(4, Math.floor(num(angleCount, 32)));
  const angleOffsets = Array.from({ length: count }, (_, index) => index * (Math.PI * 2) / count)
    .map(offset => ({ offset, nearest: Math.min(offset, Math.PI * 2 - offset) }))
    .sort((left, right) => left.nearest - right.nearest);

  const seen = new Set();
  const candidates = [];
  const validated = (x, y, ring, angleOffsetRad) => {
    const snapped = { x: persistedPositionFt(x, precisionFt), y: persistedPositionFt(y, precisionFt) };
    const key = `${snapped.x.toFixed(3)}:${snapped.y.toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (reachApproachOverlapsTarget(snapped, { x: tx, y: ty }, solid)) return;
    if (!reachApproachWithinReach(snapped, { x: tx, y: ty }, { verticalSeparationFt, sourceSizeFeet, targetSizeFeet, reachFt })) return;
    candidates.push({
      x: snapped.x,
      y: snapped.y,
      ring,
      radiusFt: Math.hypot(snapped.x - tx, snapped.y - ty),
      angleOffsetRad,
      straightDistanceFt: Math.hypot(snapped.x - ox, snapped.y - oy),
    });
  };
  const ringOrder = { boundary: 0, inset: 1, contact: 2 };
  rings.forEach(ring => {
    angleOffsets.forEach(({ offset, nearest }) => {
      const angle = nearestAngle + offset;
      validated(tx + Math.cos(angle) * ring.radiusFt, ty + Math.sin(angle) * ring.radiusFt, ring.kind, nearest);
    });
    // Exact lattice points keep a degenerate annulus (boundary == solid) reachable at diagonals; on a
    // wide annulus they are simply more boundary samples.
    if (ring.kind === 'boundary' || ring.kind === 'contact') {
      for (const lattice of reachApproachExactLatticeOffsets(ring.radiusFt, precisionFt)) {
        const angle = Math.atan2(lattice.y, lattice.x);
        let offset = angle - nearestAngle;
        offset = ((offset % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        validated(tx + lattice.x, ty + lattice.y, ring.kind, Math.min(offset, Math.PI * 2 - offset));
      }
    }
  });
  candidates.sort((left, right) => (
    (left.straightDistanceFt - right.straightDistanceFt)
    || (ringOrder[left.ring] - ringOrder[right.ring])
    || (left.angleOffsetRad - right.angleOffsetRad)
  ));
  return { candidates, rings, reason: candidates.length === 0 ? 'no-persistable-candidate' : null };
}

/**
 * The bounded nearest-first search, isolated from the scene so it can be driven by fakes.
 *   preflight(candidate)        → { accepted, positionFt, ... } | null  (scene movement preflight)
 *   landing(positionFt, preview) → { blocked, x, y }                     (ground drop resolver, target solid)
 *   legal(positionFt, preview)  → { legal, combatDistanceFt }           (range contract at the resolved point)
 *   route(positionFt, preview)  → { blocked, routeFt }                  (ground router, target solid)
 *   clearance(positionFt, routeFt) → { clears, ... }                  (reachApproachClearance)
 *   routeLengthFt(routeFt) → number
 * Returns the shortest ROUTED approach whose endpoint first enters Reach without entering the target.
 */
export function selectReachApproach({
  candidates,
  originFt,
  theoreticalMinimumFt = 0,
  maxCandidateChecks = 32,
  maxRouteChecks = 8,
  preflight,
  landing = null,
  legal,
  route,
  clearance,
  routeLengthFt,
}) {
  let winner = null;
  let firstPreview = null;
  let lastRoute = null;
  let winningDistanceFt = Number.POSITIVE_INFINITY;
  let candidateChecks = 0;
  let routeChecks = 0;
  const rejected = { preflight: 0, landing: 0, range: 0, clearance: 0, route: 0, longer: 0 };
  let truncated = false;
  // Bounded per-candidate receipt so a rejected hover can be diagnosed from the scene receipt.
  const trace = [];
  const note = (candidate, stage, detail = null) => {
    if (trace.length < 40) trace.push({ x: candidate.x, y: candidate.y, ring: candidate.ring, stage, detail });
  };
  for (const candidate of candidates) {
    if (winner && candidate.straightDistanceFt >= winningDistanceFt - 1e-6) break;
    if (candidateChecks >= maxCandidateChecks || routeChecks >= maxRouteChecks) {
      // Budget exhausted with candidates left: "no legal position" is NOT proven, only unsearched.
      truncated = winner === null;
      break;
    }
    candidateChecks += 1;
    const preview = preflight(candidate);
    if (!firstPreview && preview) firstPreview = preview;
    if (!preview?.accepted) { rejected.preflight += 1; note(candidate, 'preflight', preview?.message ?? null); continue; }
    const positionFt = { ...preview.positionFt };
    if (landing) {
      const drop = landing(positionFt, preview);
      // The ghost must land exactly where the plan posts. A nudged landing means some solid body sits
      // on this sample; the next sample is the honest answer, not a shifted ghost.
      if (drop.blocked || Math.abs(drop.x - positionFt.x) > 1e-6 || Math.abs(drop.y - positionFt.y) > 1e-6) {
        rejected.landing += 1;
        note(candidate, 'landing', { blocked: drop.blocked, x: drop.x, y: drop.y, previewZ: positionFt.z });
        continue;
      }
    }
    const legality = legal(positionFt, preview);
    if (!legality.legal) { rejected.range += 1; note(candidate, 'range', { combatDistanceFt: legality.combatDistanceFt, positionFt }); continue; }
    if (!clearance(positionFt, null).clears) { rejected.clearance += 1; note(candidate, 'clearance', null); continue; }
    if (!firstPreview.accepted) firstPreview = preview;
    routeChecks += 1;
    const routed = route(positionFt, preview);
    lastRoute = routed;
    if (routed.blocked) { rejected.route += 1; note(candidate, 'route', routed.reason ?? null); continue; }
    const routeFt = Array.isArray(routed.routeFt) && routed.routeFt.length >= 2
      ? routed.routeFt
      : [[num(originFt?.x), num(originFt?.y)], [positionFt.x, positionFt.y]];
    if (!clearance(positionFt, routeFt).clears) { rejected.clearance += 1; note(candidate, 'route-clearance', null); continue; }
    const routeDistanceFt = routeLengthFt(routeFt);
    if (routeDistanceFt >= winningDistanceFt - 1e-6) { rejected.longer += 1; note(candidate, 'longer', routeDistanceFt); continue; }
    note(candidate, 'winner', routeDistanceFt);
    winner = {
      candidate,
      positionFt,
      routeFt,
      routeDistanceFt,
      combatDistanceFt: legality.combatDistanceFt,
      preview,
      routed,
    };
    winningDistanceFt = routeDistanceFt;
    // An unobstructed nearest-boundary route is the mathematical lower bound; stop immediately. The
    // tolerance is one persisted-grid step: candidates are exact, not half-foot samples.
    if (winningDistanceFt <= theoreticalMinimumFt + 0.002) break;
  }
  return { winner, firstPreview, lastRoute, candidateChecks, routeChecks, rejected, trace, truncated };
}
